export type RegionSwitchCountry = {
  isoCode: string;
  name: string;
  marketId?: string | number;
  marketHandle?: string;
  currencyIsoCode: string;
  flagUrl?: string;
  availableLanguageIsoCodes: Array<string>;
};

type RegionSwitchCountryData = {
  currentCountryIsoCode: string;
  currentLanguageIsoCode: string;
  currentMarketId?: string | number;
  currentMarketHandle?: string;
  availableCountryCount: number;
  countries: Array<RegionSwitchCountry>;
};

const englishCountryNames = new Intl.DisplayNames(['en'], { type: 'region' });

const normalizeIso = (isoCode: string) => isoCode.toUpperCase();

const normalizeMarketHandle = (handle: string | undefined) => handle?.trim().toLowerCase() ?? '';

const SHIPPING_ESTIMATE_MARKET_HANDLE_ALIASES: Record<string, string[]> = {
  'european-union': ['eu'],
  eu: ['european-union'],
};

const resolveOverrideCodesForMarket = (
  marketHandle: string,
  overrides: Record<string, string[]>,
): string[] => {
  const codes = new Set<string>();
  const keys = [marketHandle, ...(SHIPPING_ESTIMATE_MARKET_HANDLE_ALIASES[marketHandle] ?? [])];

  for (const key of keys) {
    for (const code of overrides[key] ?? []) {
      codes.add(code);
    }
  }

  return [...codes];
};

const resolveAllOverrideCodes = (overrides: Record<string, string[]>): string[] => [
  ...new Set(Object.values(overrides).flat()),
];

export const isEuMarketHandle = (handle: string | undefined): boolean => {
  const normalized = normalizeMarketHandle(handle);

  return normalized === 'european-union' || normalized === 'eu';
};

export const getMarketCountryIsoCodes = (
  marketHandle: string | undefined,
  baseIsoCodes: string[],
  marketCountryOverrides: Record<string, string[]> = {},
): string[] => {
  const normalizedHandle = normalizeMarketHandle(marketHandle);
  const configuredCodes = resolveOverrideCodesForMarket(normalizedHandle, marketCountryOverrides);

  if (configuredCodes.length > 0) {
    return configuredCodes;
  }

  return baseIsoCodes.map((isoCode) => normalizeIso(isoCode)).filter(Boolean);
};

export const parseShippingEstimateMarketCountryOverrides = (
  raw: string | undefined,
): Record<string, string[]> => {
  if (!raw?.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, string>;

    return Object.fromEntries(
      Object.entries(parsed).map(([handle, codes]) => [
        normalizeMarketHandle(handle),
        codes
          .split(',')
          .map((code) => normalizeIso(code))
          .filter(Boolean),
      ]),
    );
  } catch {
    return {};
  }
};

const buildCountryEntry = (isoCode: string, data: RegionSwitchCountryData): RegionSwitchCountry => {
  const fromStore = data.countries.find((country) => normalizeIso(country.isoCode) === isoCode);

  return (
    fromStore ?? {
      isoCode,
      name: englishCountryNames.of(isoCode) ?? isoCode,
      currencyIsoCode: '',
      availableLanguageIsoCodes: [],
    }
  );
};

export const getShippingEstimateCountries = (
  data: RegionSwitchCountryData | undefined,
  siteRegions: string[] = [],
  marketCountryOverrides: Record<string, string[]> = {},
): RegionSwitchCountry[] => {
  if (!data) {
    return [];
  }

  const marketHandle = normalizeMarketHandle(data.currentMarketHandle);
  const byIso = new Map<string, RegionSwitchCountry>();

  for (const country of data.countries) {
    byIso.set(normalizeIso(country.isoCode), country);
  }

  const overrideCodes = [
    ...new Set([
      ...resolveAllOverrideCodes(marketCountryOverrides),
      ...resolveOverrideCodesForMarket(marketHandle, marketCountryOverrides),
    ]),
  ];

  for (const isoCode of overrideCodes) {
    if (!byIso.has(isoCode)) {
      byIso.set(isoCode, buildCountryEntry(isoCode, data));
    }
  }

  for (const region of siteRegions) {
    const isoCode = normalizeIso(region);

    if (!isoCode || isoCode === 'DEFAULT' || byIso.has(isoCode)) {
      continue;
    }

    byIso.set(isoCode, buildCountryEntry(isoCode, data));
  }

  return [...byIso.values()].sort((a, b) => a.name.localeCompare(b.name));
};

export function getRegionSwitchCountryData(root: ParentNode = document) {
  const script = root.querySelector<HTMLScriptElement>('[data-region-switch-countries]');

  if (!script?.textContent) {
    return undefined;
  }

  try {
    return JSON.parse(script.textContent) as RegionSwitchCountryData;
  } catch {
    return undefined;
  }
}
